<script setup lang="ts">
import { computed } from 'vue'
import {
  CheckIcon,
  CheckSquareIcon,
  ChevronRightIcon,
  DollarSignIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  PencilIcon,
} from '@lucide/vue'

import { LinkedDataAccount } from '@types'

import {
  DSButton,
  DSCheckbox,
  DSDropdownMenu,
  DSDropdownMenuContent,
  DSDropdownMenuItem,
  DSDropdownMenuSeparator,
  DSDropdownMenuTrigger,
  DSSwitch,
} from '@ds'

import AssetTreeRowLinkedData from './AssetTreeRowLinkedData.vue'
import AssetTreeRowName from './AssetTreeRowName.vue'
import { AssetTreeConnectorInfo, AssetTreeItem } from './utils/types'

type Props = {
  item: AssetTreeItem
  isRoot: boolean
  hasChildren: boolean
  isExpanded: boolean
  isLast: boolean
  ancestorContinues: boolean[]
  query: string
  selecting: boolean
  selected: boolean
  isActiveMatch: boolean
  isActive: boolean
  hideActions: boolean
  readonly: boolean
  isFiltered: boolean
  ownershipAdjusted: boolean
  connectorInfo?: AssetTreeConnectorInfo
  getLinkedAccountInfo?: (key: string) => Promise<LinkedDataAccount> | undefined
  balanceLabel: string
  amountLabel?: string
}

const {
  item, isRoot, hasChildren, isExpanded, isLast, ancestorContinues, query, selecting, selected, isActiveMatch,
  isActive, hideActions, readonly, isFiltered, ownershipAdjusted, connectorInfo, balanceLabel, amountLabel,
  getLinkedAccountInfo,
} = defineProps<Props>()

const emit = defineEmits<{
  select: []
  'click:name': []
  'menu:select': []
  'menu:edit': []
  'menu:set-price': []
  'menu:filter': []
  'menu:ownership-toggle': []
  'menu:edit-repository': []
}>()

const isEditable = computed(() => !isRoot && !readonly)

const nameParts = computed(() => highlightParts(item.name))
const descriptionParts = computed(() => item.description ? highlightParts(item.description) : undefined)

// The current match (via the search box's prev/next) stands out from the rest, same as a
// browser's "find in page" treats the active hit differently from other highlighted ones.
const matchHighlightClass = computed(() => isActiveMatch
  ? 'bg-primary text-primary-foreground rounded-ds-sm'
  : 'bg-primary/20 rounded-ds-sm')

function highlightParts(text: string) {
  const needle = query.trim()
  if (!needle) return [{ text, highlighted: false }]
  const haystack = text.toLowerCase()
  const lowerNeedle = needle.toLowerCase()
  const parts: { text: string, highlighted: boolean }[] = []
  let cursor = 0
  while (cursor < text.length) {
    const matchAt = haystack.indexOf(lowerNeedle, cursor)
    if (matchAt < 0) {
      parts.push({ text: text.slice(cursor), highlighted: false })
      break
    }
    if (matchAt > cursor) parts.push({ text: text.slice(cursor, matchAt), highlighted: false })
    parts.push({ text: text.slice(matchAt, matchAt + needle.length), highlighted: true })
    cursor = matchAt + needle.length
  }
  return parts
}

/*
 * Branch rows use Reka's `TreeItem` root for expand/collapse;
 * children elements (name, checkbox, etc.) stop propagation. 
 * Leaf rows use `handleRowClick` for full-row name actions.
 */
const handleRowClick = () => {
  if (!hasChildren) emit('click:name')
}

const handleNameClick = (event: MouseEvent) => {
  event.stopPropagation()
  emit('click:name')
}
</script>

<template>
  <div
    :data-asset-tree-row="item.key"
    class="group relative flex items-start gap-0.5 pl-1.5 pr-1 py-1.25 rounded-ds-md cursor-pointer hover:bg-background dark:hover:bg-muted"
    :class="[
      (selected || isActive) && 'bg-accent dark:bg-primary/30',
      isActiveMatch && 'ring-1 ring-inset ring-primary/10',
    ]"
    @click="handleRowClick"
  >
    <div
      v-if="selecting && !hideActions"
      class="w-5 shrink-0 self-stretch flex items-start pt-0.5"
      @click.stop
    >
      <DSCheckbox
        v-if="isEditable"
        :model-value="selected"
        @update:model-value="emit('select')"
      />
    </div>

    <span
      v-for="(continues, index) in ancestorContinues"
      :key="index"
      class="w-4 shrink-0 self-stretch relative"
    >
      <span
        v-if="continues"
        class="absolute -inset-y-1.25 left-1/2 w-px bg-border"
      />
    </span>

    <div
      v-if="!isRoot"
      class="relative w-4 self-stretch shrink-0"
    >
      <span
        class="absolute left-1/2 -top-1.25 w-px bg-border"
        :class="isLast ? 'h-4' : '-bottom-1.25'"
      />
      <span class="absolute left-1/2 top-2.75 h-px w-2.25 bg-border" />
    </div>

    <div class="relative w-4 self-stretch shrink-0 mr-1">
      <div
        class="h-5.5 flex items-center justify-center text-muted-foreground"
      >
        <div
          v-if="hasChildren"
          class="size-5 -m-0.5 rounded-ds-sm flex items-center justify-center hover:bg-secondary dark:hover:bg-muted-foreground/10 hover:text-foreground"
        >
          <ChevronRightIcon
            class="size-3.5 transition-transform duration-ds-fast ease-ds-standard"
            :class="isExpanded && 'rotate-90'"
            aria-hidden="true"
          />
        </div>
        <span
          v-else
          class="size-1 rounded-full bg-muted-foreground/50"
        />
      </div>
      <span
        v-if="isExpanded"
        class="absolute top-5.5 -bottom-1.25 left-1/2 w-px bg-border"
      />
    </div>

    <div class="flex-1 min-w-0 flex flex-col gap-0.5 py-px">
      <AssetTreeRowName
        :id="item.path.at(-1) as string"
        :name="item.name"
        class="text-sm font-medium text-foreground"
        @click="handleNameClick"
      >
        <span
          v-for="(part, index) in nameParts"
          :key="index"
          :class="part.highlighted && matchHighlightClass"
        >{{ part.text }}</span>
      </AssetTreeRowName>
      <span
        v-if="item.description"
        class="text-xs font-medium text-muted-foreground/75 truncate"
      >
        <span
          v-for="(part, index) in descriptionParts"
          :key="index"
          :class="part.highlighted && matchHighlightClass"
        >{{ part.text }}</span>
      </span>
    </div>

    <AssetTreeRowLinkedData
      v-if="connectorInfo"
      :asset-id="item.path.at(-1) as string"
      :connector-info="connectorInfo"
      :get-linked-account-info="getLinkedAccountInfo"
    />

    <div class="shrink-0 w-21 py-0.5 flex flex-col items-end gap-0.5 tabular-nums whitespace-nowrap">
      <span class="text-sm font-medium text-foreground">{{ balanceLabel }}</span>
      <span
        v-if="!isRoot && amountLabel"
        class="text-xs font-medium text-muted-foreground/75"
      >{{ amountLabel }}</span>
    </div>

    <DSDropdownMenu v-if="!hideActions">
      <DSDropdownMenuTrigger
        as-child
        @click.stop
      >
        <DSButton
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Actions"
          class="size-6.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <EllipsisVerticalIcon aria-hidden="true" />
        </DSButton>
      </DSDropdownMenuTrigger>
      <DSDropdownMenuContent align="end">
        <DSDropdownMenuItem
          v-if="isEditable"
          @click="emit('menu:select')"
        >
          <CheckSquareIcon aria-hidden="true" />
          <span class="flex-1">{{ selected ? 'Deselect' : 'Select' }}</span>
        </DSDropdownMenuItem>
        <label
          v-if="isRoot"
          class="flex items-center gap-2 p-1 text-sm cursor-pointer"
        >
          <DSSwitch
            :model-value="ownershipAdjusted"
            size="sm"
            @click="emit('menu:ownership-toggle')"
          />
          <span class="flex-1">Ownership adjusted</span>
        </label>
        <DSDropdownMenuItem
          v-if="hasChildren"
          :disabled="isFiltered"
          @click="emit('menu:filter')"
        >
          <span class="relative">
            <FunnelIcon
              class="size-4"
              aria-hidden="true"
            />
            <span
              v-if="isFiltered"
              class="size-2.5 rounded-full absolute -right-0.5 -top-0.5 flex items-center justify-center bg-foreground"
            >
              <CheckIcon
                class="size-2 text-background"
                aria-hidden="true"
              />
            </span>
          </span>
          <span class="flex-1">Filter unlocked dashboards</span>
        </DSDropdownMenuItem>
        <DSDropdownMenuSeparator />
        <template v-if="isEditable">
          <DSDropdownMenuItem @click="emit('menu:edit')">
            <PencilIcon aria-hidden="true" />
            Edit
          </DSDropdownMenuItem>
        </template>
        <template v-if="isRoot">
          <DSDropdownMenuItem @click="emit('menu:edit-repository')">
            <PencilIcon aria-hidden="true" />
            Edit repository
          </DSDropdownMenuItem>
        </template>
        <DSDropdownMenuItem
          v-if="!hasChildren"
          @click="emit('menu:set-price')"
        >
          <DollarSignIcon aria-hidden="true" />
          Set price
        </DSDropdownMenuItem>
      </DSDropdownMenuContent>
    </DSDropdownMenu>
  </div>
</template>
